import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import type { ExportDataset, ExportFormat } from '@/types/portal';

interface OptionCardProps {
  active: boolean;
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}

function OptionCard({ active, icon, title, description, onClick }: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors cursor-pointer ${
        active
          ? 'border-primary-400 bg-primary-50'
          : 'border-background-300 bg-background-50 hover:border-background-400'
      }`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base ${
          active ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-600'
        }`}
      >
        <i className={icon} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground-950">{title}</span>
        <span className="block text-xs text-foreground-500">{description}</span>
      </span>
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          active ? 'border-primary-500 bg-primary-500 text-background-50' : 'border-background-400'
        }`}
      >
        {active ? <i className="ri-check-line text-[10px]" /> : null}
      </span>
    </button>
  );
}

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (format: ExportFormat, dataset: ExportDataset) => void;
  currentTabLabel: string;
}

export default function ExportModal({
  open,
  onClose,
  onExport,
  currentTabLabel,
}: ExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [dataset, setDataset] = useState<ExportDataset>('current');

  useEffect(() => {
    if (open) {
      setFormat('csv');
      setDataset('current');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-download-2-line"
      title="Export report"
      subtitle="Choose a format and what to include"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon={format === 'csv' ? 'ri-file-excel-2-line' : 'ri-file-pdf-2-line'}
            onClick={() => onExport(format, dataset)}
          >
            Export {format.toUpperCase()}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <p className="mb-2 text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">
            Format
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <OptionCard
              active={format === 'csv'}
              icon="ri-file-excel-2-line"
              title="CSV"
              description="Raw data for spreadsheets and further analysis"
              onClick={() => setFormat('csv')}
            />
            <OptionCard
              active={format === 'pdf'}
              icon="ri-file-pdf-2-line"
              title="PDF"
              description="Formatted document — print or save as PDF"
              onClick={() => setFormat('pdf')}
            />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">
            Include
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <OptionCard
              active={dataset === 'current'}
              icon="ri-filter-3-line"
              title="Current view"
              description={`${currentTabLabel} only, with the active filters`}
              onClick={() => setDataset('current')}
            />
            <OptionCard
              active={dataset === 'all'}
              icon="ri-stack-line"
              title="Full report"
              description="Campaign and creator performance together"
              onClick={() => setDataset('all')}
            />
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
          <i className="ri-information-line mt-0.5 text-foreground-500" />
          <p className="text-xs text-foreground-600">
            Exports respect the current period and market filters. PDF opens your browser print
            dialog — choose “Save as PDF” to keep a copy.
          </p>
        </div>
      </div>
    </Modal>
  );
}